import { useState, useEffect, useCallback, useMemo } from 'react';
import { History, RefreshCw, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const STATUS_MAP = {
  done: { label: 'Terminé', cls: 'text-[#00b67a] bg-green-50' },
  stopped: { label: 'Arrêté', cls: 'text-orange-600 bg-orange-50' },
  error: { label: 'Erreur', cls: 'text-red-600 bg-red-50' },
};

const PRESET_LABEL = {
  excellent: 'Excellent', positive: 'Positif', realistic: 'Réaliste', mixed: 'Mitigé',
};

export const GenerationLog = ({ refreshKey }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/generation-log?limit=50`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const companyOptions = useMemo(() => {
    const names = [...new Set(logs.map((l) => l.company_name).filter(Boolean))];
    names.sort((a, b) => a.localeCompare(b, 'fr'));
    return names;
  }, [logs]);

  const filteredLogs = useMemo(() => logs.filter((l) => {
    if (companyFilter !== 'all' && l.company_name !== companyFilter) return false;
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    return true;
  }), [logs, companyFilter, statusFilter]);

  return (
    <div className="bg-white rounded-lg border mt-8" data-testid="generation-log">
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-[#1a1a1a] flex items-center gap-2">
          <History className="w-5 h-5 text-[#00b67a]" />
          Journal des générations
          <span className="text-sm font-normal text-gray-500">({filteredLogs.length})</span>
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
            data-testid="log-filter-company"
          >
            <option value="all">Toutes les entreprises</option>
            {companyOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
            data-testid="log-filter-status"
          >
            <option value="all">Tous les statuts</option>
            <option value="done">Terminé</option>
            <option value="stopped">Arrêté</option>
            <option value="error">Erreur</option>
          </select>
          <button onClick={load} className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#00b67a] transition-colors" data-testid="log-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm" data-testid="log-empty">
          {logs.length === 0 ? "Aucune génération enregistrée pour l'instant." : 'Aucune génération ne correspond aux filtres.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Entreprise</th>
                <th className="px-4 py-2 font-medium">Créés</th>
                <th className="px-4 py-2 font-medium">Notes</th>
                <th className="px-4 py-2 font-medium">Période</th>
                <th className="px-4 py-2 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((l) => {
                const st = STATUS_MAP[l.status] || { label: l.status, cls: 'text-gray-600 bg-gray-50' };
                return (
                  <tr key={l.id} className="border-b last:border-0" data-testid={`log-row-${l.id}`}>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDateTime(l.finished_at)}</td>
                    <td className="px-4 py-2 text-[#1a1a1a] font-medium">
                      {l.company_name}
                      {l.scope === 'all' && <span className="ml-1 text-xs text-gray-400">(lot global)</span>}
                      {l.scope === 'daily' && <span className="ml-1 text-xs text-[#00b67a]">(auto quotidien)</span>}
                    </td>
                    <td className="px-4 py-2 text-[#00b67a] font-semibold">+{l.created}</td>
                    <td className="px-4 py-2 text-gray-600">{PRESET_LABEL[l.rating_preset] || l.rating_preset || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap text-xs">{l.date_from} → {l.date_to}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GenerationLog;
